import { defineStore } from 'pinia'
import type { IReportDescription } from '@team-milva/lab-models/resources'
import { collection, doc, onSnapshot, query, updateDoc, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'
import { ReportDescription } from '@/globals/typescript/models/ReportDescription'
import type { TLibraryVisibilityFilter } from '@/globals/typescript/utils/libraryVisibilityFilter'

export { ReportDescription }

type CreateReportDescriptionParams = {
  name: string
  usesDescriptions?: boolean
}

type UpdateReportDescriptionParams = {
  id: string
  activeState?: 'active' | 'inactive' | 'archived'
  usesDescriptions?: boolean
}

export const useReportDescriptionStore = defineStore('reportDescription', {
  state: () => ({
    allReportDescriptions: [] as ReportDescription[],
    unsubscribeAllReportDescriptions: null as (() => void) | null,
    // Which states the descriptions list shows. Upgraded to the shared
    // 4-option library visibility filter now that ReportDescription itself
    // has an archived state, matching reportDescriptionItemStore/
    // textListStore.
    listStateFilter: 'active' as TLibraryVisibilityFilter,
  }),

  getters: {
    allReportDescriptionsLoaded(): boolean {
      return !!this.unsubscribeAllReportDescriptions
    },
  },

  actions: {
    getReportDescriptions(): Promise<ReportDescription[]> {
      if (this.allReportDescriptionsLoaded) {
        return Promise.resolve(this.allReportDescriptions)
      }

      return new Promise((resolve) => {
        let resolved = false
        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'ReportDescriptions'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allReportDescriptions = querySnapshot.docs
            .map(
              (doc) => new ReportDescription(doc.data() as IReportDescription)
            )
            .sort((a, b) => a.timestamps.createdAt - b.timestamps.createdAt)

          this.unsubscribeAllReportDescriptions = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(this.allReportDescriptions)
          }
        })
      })
    },

    async createReportDescription(
      params: CreateReportDescriptionParams
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/report-descriptions',
        {
          path: { labAccountID },
          // TODO: remove cast after deploying and re-running openapi:gen —
          // usesDescriptions is optional in the real Zod schema now, but the
          // generated codegen types still reflect the currently-deployed spec,
          // which only knows about `name`.
          body: {
            name: params.name,
            usesDescriptions: params.usesDescriptions,
          } as { name: string },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateReportDescription(
      params: UpdateReportDescriptionParams
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      // TODO: remove cast after deploying and re-running openapi:gen — the
      // generated codegen types still reflect the currently-deployed spec,
      // which requires activeState (as 'active'|'inactive' only, no
      // 'archived' yet) and doesn't know about usesDescriptions at all.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'patch',
        'lab-accounts/{labAccountID}/report-descriptions/{reportDescriptionID}',
        {
          path: { labAccountID, reportDescriptionID: params.id },
          body: {
            activeState: params.activeState,
            usesDescriptions: params.usesDescriptions,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    // Writes directly to Firestore rather than through managementApi, matching
    // how a TextList's own name is renamed from the Labels Library
    // (textListStore.updateCustomListName) — the same shape of inline
    // collection-title edit. Guarded by the ReportDescriptions rule in
    // firestore.rules, scoped to ['name', 'timestamps'].
    async updateReportDescriptionName(
      id: string,
      name: IReportDescription['name']
    ): Promise<void> {
      if (!Object.values(name).some((v) => v !== '')) return
      const docRef = doc(db, 'Apps', '-lab-management-', 'ReportDescriptions', id)
      await updateDoc(docRef, { name, 'timestamps.updatedAt': Date.now() })
    },

    async updateReportDescriptionUsesDescriptions(
      id: string,
      usesDescriptions: boolean
    ): Promise<void> {
      await this.updateReportDescription({ id, usesDescriptions })
    },

    async deactivateReportDescription(id: string): Promise<void> {
      await this.updateReportDescription({ id, activeState: 'inactive' })
    },

    async reactivateReportDescription(id: string): Promise<void> {
      await this.updateReportDescription({ id, activeState: 'active' })
    },

    async archiveReportDescription(id: string): Promise<void> {
      await this.updateReportDescription({ id, activeState: 'archived' })
    },

    resetStore(): void {
      this.allReportDescriptions = []
      this.listStateFilter = 'active'
      if (this.unsubscribeAllReportDescriptions) {
        this.unsubscribeAllReportDescriptions()
        this.unsubscribeAllReportDescriptions = null
      }
    },
  },
})

